import { useMemo, useRef, useState } from 'react'
import {
  Box, Breadcrumbs, Typography, Stack, Button, Chip, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Snackbar, Alert,
} from '@mui/material'
import { Add, MoreVert, Edit, Visibility, RuleFolder, Cancel, PriceChange, UploadFile, Download } from '@mui/icons-material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  listContracts, submitContractForApproval, terminateContract, uploadContractDocument, getContractDocument,
  type SupplierContractListItem, type SupplierContractStatus,
} from '../../api/contracte'
import { errorMessage } from '../../api/client'
import ContractFormDialog from './ContractFormDialog'
import AddContractLineDialog from './AddContractLineDialog'

const STATUS_LABEL: Record<SupplierContractStatus, string> = {
  Draft: 'Ciornă', PendingApproval: 'În aprobare', Activ: 'Activ', Expirat: 'Expirat', Reziliat: 'Reziliat',
}
const STATUS_COLOR: Record<SupplierContractStatus, 'default' | 'success' | 'warning' | 'error'> = {
  Draft: 'default', PendingApproval: 'warning', Activ: 'success', Expirat: 'error', Reziliat: 'error',
}
const TYPE_LABEL: Record<string, string> = {
  CadruPret: 'Cadru de preț', AngajamentVolum: 'Angajament de volum', Servicii: 'Servicii', Mentenanta: 'Mentenanță',
}

export default function ContractsPage() {
  const [formTarget, setFormTarget] = useState<SupplierContractListItem | null | 'new'>(null)
  const [addLineTarget, setAddLineTarget] = useState<SupplierContractListItem | null>(null)
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; row: SupplierContractListItem } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['achizitii', 'contracte'], queryFn: listContracts })
  const closeMenu = () => setMenuAnchor(null)
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['achizitii', 'contracte'] })

  const submitMutation = useMutation({
    mutationFn: submitContractForApproval,
    onSuccess: (r) => { invalidate(); setError(r.pendingApproval ? 'Contract trimis la aprobare.' : null) },
    onError: (e) => setError(errorMessage(e)),
  })
  const terminateMutation = useMutation({ mutationFn: terminateContract, onSuccess: invalidate, onError: (e) => setError(errorMessage(e)) })

  const fileInputRef = useRef<HTMLInputElement>(null)
  const uploadTargetId = useRef<string | null>(null)
  const uploadMutation = useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => uploadContractDocument(id, file),
    onError: (e) => setError(errorMessage(e)),
  })
  const downloadDocument = async (id: string) => {
    try {
      const blob = await getContractDocument(id)
      window.open(URL.createObjectURL(blob), '_blank')
    } catch (e) {
      setError(errorMessage(e))
    }
  }

  const columns = useMemo<GridColDef<SupplierContractListItem>[]>(() => [
    { field: 'number', headerName: 'Nr.', width: 130, renderCell: (p) => <code>{p.row.number}</code> },
    { field: 'supplierName', headerName: 'Furnizor', flex: 1, minWidth: 180 },
    { field: 'type', headerName: 'Tip', width: 160, valueGetter: (v: string) => TYPE_LABEL[v] ?? v },
    { field: 'startDate', headerName: 'Început', width: 110 },
    { field: 'endDate', headerName: 'Sfârșit', width: 110 },
    {
      field: 'status', headerName: 'Status', width: 190, sortable: false,
      renderCell: (p) => (
        <Stack direction="row" spacing={0.5}>
          <Chip size="small" label={STATUS_LABEL[p.row.status]} color={STATUS_COLOR[p.row.status]} variant="outlined" />
          {p.row.isExpiringSoon && <Chip size="small" label="Expiră curând" color="warning" />}
        </Stack>
      ),
    },
    {
      field: 'actions', headerName: '', width: 56, sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (p) => (
        <IconButton size="small" onClick={(e) => setMenuAnchor({ el: e.currentTarget, row: p.row })}>
          <MoreVert fontSize="small" />
        </IconButton>
      ),
    },
  ], [])

  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Achiziții</Typography>
        <Typography variant="inherit" color="text.primary">Contracte furnizori</Typography>
      </Breadcrumbs>
      <Typography variant="h2">Contracte furnizori</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setFormTarget('new')}>Contract nou</Button>
      </Box>
      <Box sx={{ height: 560, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>

      <ContractFormDialog target={formTarget} onClose={() => setFormTarget(null)} />
      <AddContractLineDialog contract={addLineTarget} onClose={() => setAddLineTarget(null)} onAdded={invalidate} />

      <Menu anchorEl={menuAnchor?.el} open={!!menuAnchor} onClose={closeMenu}>
        {menuAnchor && [
          <MenuItem key="edit" onClick={() => { setFormTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon>{menuAnchor.row.status === 'Draft' ? <Edit fontSize="small" /> : <Visibility fontSize="small" />}</ListItemIcon>
            <ListItemText>{menuAnchor.row.status === 'Draft' ? 'Editare' : 'Vizualizare'}</ListItemText>
          </MenuItem>,
          <MenuItem key="submit" disabled={menuAnchor.row.status !== 'Draft'}
            onClick={() => { submitMutation.mutate(menuAnchor.row.id); closeMenu() }}>
            <ListItemIcon><RuleFolder fontSize="small" /></ListItemIcon><ListItemText>Trimite la aprobare</ListItemText>
          </MenuItem>,
          <MenuItem key="addLine" disabled={!['Activ', 'Draft'].includes(menuAnchor.row.status)}
            onClick={() => { setAddLineTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><PriceChange fontSize="small" /></ListItemIcon><ListItemText>Preț nou (act adițional)</ListItemText>
          </MenuItem>,
          <MenuItem key="terminate" disabled={!['Activ', 'PendingApproval'].includes(menuAnchor.row.status)}
            onClick={() => { terminateMutation.mutate(menuAnchor.row.id); closeMenu() }}>
            <ListItemIcon><Cancel fontSize="small" /></ListItemIcon><ListItemText>Reziliază</ListItemText>
          </MenuItem>,
          <MenuItem key="upload"
            onClick={() => { uploadTargetId.current = menuAnchor.row.id; fileInputRef.current?.click(); closeMenu() }}>
            <ListItemIcon><UploadFile fontSize="small" /></ListItemIcon><ListItemText>Atașează document</ListItemText>
          </MenuItem>,
          <MenuItem key="download" onClick={() => { void downloadDocument(menuAnchor.row.id); closeMenu() }}>
            <ListItemIcon><Download fontSize="small" /></ListItemIcon><ListItemText>Descarcă document</ListItemText>
          </MenuItem>,
        ]}
      </Menu>

      <input ref={fileInputRef} type="file" hidden onChange={(e) => {
        const file = e.target.files?.[0]
        if (file && uploadTargetId.current) uploadMutation.mutate({ id: uploadTargetId.current, file })
        e.target.value = ''
      }} />

      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError(null)}>
        <Alert severity={error?.includes('aprobare') ? 'info' : 'error'} onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </Stack>
  )
}
